import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import * as XLSX from "xlsx";
import Tooltip from "@mui/material/Tooltip";
// import { CSVLink } from "react-csv";
import XlsxIcon from "../svg/XlsxIcon"; // Excel icon
import CsvIcon from "../svg/CsvIcon"; // CSV icon
import apiUtils from "../utils/apiUtils";
import axios from "axios";
import moment from "moment";
import { resolveVatClaimPayoutDate, resolveExciseClaimPayoutDate } from "../utils/claimPayoutDateUtils";
import { formatDisplayDate, formatExportDateForCsv, parseExportDate } from "../utils/dateFormatUtils";
import {
  getInvoiceListLabelDisplayValue,
  getInvoiceListStatusDisplayValue,
} from "../utils/invoiceStatusUtils";
import { translateExciseClaimStatus } from "../utils/claimStatusUtils";

const EXPORT_DATE_FIELDS = {
  invoices: ["invoiceDate", "preFinancedDateSortable"],
  claims: ["claimDateSortable", "reimbursementDateSortable", "payDateSortable"],
  exciseClaims: ["claimDate", "reimbursementDate", "payDateSortable", "preFinanceDate"],
};

const EXCEL_DATE_FORMAT = "dd-mm-yyyy";

const DataGridExport = ({
  tableData,
  columns,
  exportType = "default",
  customerId,
  customerNumber,
  claimData = [],
  customerPreFinanceData = [],
}) => {
  const { t } = useTranslation();
  // console.log("tableData", tableData);
  // console.log("columns", columns);
  // console.log("customerId:", customerId);
  // console.log("customerNumber:", customerNumber);
  // console.log("First row data:", tableData[0]);

  const countryData = apiUtils.useApiData("/api/v1/countries");
  const currencyData = apiUtils.useApiData("/api/v1/currencies");
  const [preFinanceData, setPreFinanceData] = useState([]);

  useEffect(() => {
    // console.log("useEffect triggered with customerId:", customerId);
    if (customerId) {
      // console.log("Fetching preFinance data for customerId:", customerId);
      axios
        .get("/api/v1/preFinanceByEntityId/" + customerId)
        .then((response) => {
          // console.log("PreFinance data received:", response.data);
          setPreFinanceData(response.data);
        })
        .catch((error) => console.error("Error fetching preFinance data:", error));
    } else {
      // console.log("No customerId provided");
    }
  }, [customerId]);

  const filteredColumns = columns.filter((column) => {
    if (column.field === "actions") {
      return false;
    }

    if (exportType === "exciseClaims" && column.field === "claimId") {
      return false;
    }

    return true;
  });

  const nonRealDates = ["0000-00-00", "01-01-1900", "01-01-1970", "00-00-0000", "01-01-0001", "0001-01-01", "0001-01-01T00:00:00"];

  const nonDateExportLabels = [t("N/A"), t("Pre-Financed")];

  const toExcelDateCellValue = (value) => {
    if (value == null || value === "" || nonDateExportLabels.includes(String(value))) {
      return value ?? "";
    }

    const parsed = parseExportDate(value);
    return parsed ?? value;
  };

  const isValidDate = (dateString) => {
    if (!dateString || typeof dateString !== "string") return false;

    const normalizedDate = dateString.split("T")[0];

    if (nonRealDates.includes(normalizedDate)) return false;

    const date = moment(dateString, moment.ISO_8601, true);

    return date.isValid() && date.year() > 1970;
  };

  const formatExciseDateForExport = (dateString, claimCode) => {
    if (!claimCode) return t("N/A");

    const lastTwoDigits = claimCode.slice(-2);

    if (lastTwoDigits === "11") {
      return t("N/A");
    }

    if (!isValidDate(dateString)) {
      return t("N/A");
    }

    return formatDisplayDate(dateString);
  };

  // const formatCurrency = (amount) => {
  //   if (!amount && amount !== 0) return "€ 0,00";
  //   const formatted = new Intl.NumberFormat("nl-NL", {
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2,
  //     useGrouping: true,
  //   }).format(amount);
  //   return `€ ${formatted}`;
  // };

  const transformData = (data) => {
    switch (exportType) {
      case "invoices":
        return data.map((row) => {
          const nonRealDates = [
            "0000-00-00",
            "01-01-1900",
            "01-01-1970",
            "00-00-0000",
            "01-01-0001",
            "0001-01-01",
            "01-01-1",
            "0001-01-01T00:00:00",
            "1-1-1",
            "01-1-1",
            "1-01-1",
          ];

          // Get the payout date from the row
          let payoutDate = "";
          const { invoiceStateId, invoiceVATAmountLocalCurrency, claimId, preFinanceId } = row;
          let invoiceClaimData = claimData.find((claim) => claim.claimId === claimId);

          if (
            !(
              invoiceStateId === 4 ||
              invoiceVATAmountLocalCurrency === 0 ||
              invoiceClaimData?.claimStateId === 1 ||
              invoiceClaimData?.claimStateId === 3
            )
          ) {
            if (invoiceClaimData) {
              const rowData = data.find((data) => data.claimId === invoiceClaimData.claimId);
              const lastTwoDigits = invoiceClaimData.claimCode.slice(-2);

              if (lastTwoDigits === "11") {
                if (rowData?.preFinanceId > 0) {
                  let foundPreFinance = customerPreFinanceData.find((data) => data.preFinanceId === rowData.preFinanceId);
                  if (foundPreFinance && foundPreFinance.payoutDate && !nonRealDates.includes(foundPreFinance.payoutDate)) {
                    payoutDate = formatDisplayDate(foundPreFinance.payoutDate);
                  }
                }
              } else if (lastTwoDigits === "01") {
                if (!nonRealDates.includes(invoiceClaimData.payDate)) {
                  payoutDate = formatDisplayDate(invoiceClaimData.payDate);
                }
              } else if (lastTwoDigits === "00") {
                if (
                  !(invoiceClaimData?.claimStateId === 4 && preFinanceId === 0) &&
                  !nonRealDates.includes(invoiceClaimData.reimbursementDate)
                ) {
                  payoutDate = formatDisplayDate(invoiceClaimData.reimbursementDate);
                }
              }
            }
          }

          return {
            ...row,
            invoiceDate: formatDisplayDate(row.invoiceDate),
            invoiceStatusDesc: getInvoiceListStatusDisplayValue(
              row,
              new Map(claimData.map((claim) => [claim.claimId, claim])),
              t
            ),
            invoiceLabelDesc: getInvoiceListLabelDisplayValue(row, t),
            vatAmount: row.vatAmount || 0,
            preFinanced: row.preFinanced === 1 ? t("Yes") : t("No"),
            preFinancedDateSortable: payoutDate,
          };
        });
      case "claims": {
        return data.map((row) => {
          const lastTwoDigits = row.claimCode ? row.claimCode.slice(-2) : "";
          const shouldShowNA = ["11", "10"].includes(lastTwoDigits) || nonRealDates.includes(row.claimDate);
          const shouldShowRefundNA = ["01", "10", "11"].includes(lastTwoDigits) || nonRealDates.includes(row.notificationDate);

          // Payout Date — same rules as ListClaims payDateSortable renderCell
          const payoutDateResolved = resolveVatClaimPayoutDate(row);
          let payoutDate = "";
          if (payoutDateResolved.kind === "na") {
            payoutDate = t("N/A");
          } else if (payoutDateResolved.kind === "preFinanced") {
            payoutDate = t("Pre-Financed");
          } else {
            payoutDate = formatDisplayDate(payoutDateResolved.value);
          }

          const selectedCountry = countryData?.find((country) => country.countryId === row.countryId);
          const vatEurAsInGrid =
            selectedCountry != null
              ? (row.claimVATAmountLocCur || 0) * (selectedCountry.exchangeRate ?? 0)
              : row.claimVATAmountEUR || 0;

          const padMonth = (value) => (value != null && value !== "" ? String(value).padStart(2, "0") : "");

          return {
            ...row,
            startMonth: padMonth(row.startMonth),
            endMonth: padMonth(row.endMonth),
            claimStateDescription: row.claimStateDescription ? t(row.claimStateDescription) : "",
            claimDateSortable: shouldShowNA ? t("N/A") : formatDisplayDate(row.claimDate),
            reimbursementDateSortable: shouldShowRefundNA ? t("N/A") : formatDisplayDate(row.notificationDate),
            payDateSortable: payoutDate,
            vatAmount: row.vatAmount || 0,
            claimVATAmountEUR: vatEurAsInGrid,
            claimVATAmountLocCur: row.claimVATAmountLocCur || 0,
          };
        });
      }
      case "exciseClaims":
        return data.map((row) => {
          const selectedCountry = countryData.find((country) => country.countryId === row.countryId);
          const preFinance = preFinanceData.find((item) => item.preFinanceId === row.preFinanceId);
          const selectedCurrency = currencyData.find((currency) => currency.currencyId === selectedCountry?.currency);
          const payoutDateResolved = resolveExciseClaimPayoutDate(row);
          let payoutDate = "";
          if (payoutDateResolved.kind === "na") {
            payoutDate = t("N/A");
          } else if (payoutDateResolved.kind === "preFinanced") {
            payoutDate = t("Pre-Financed");
          } else {
            payoutDate = formatDisplayDate(payoutDateResolved.value);
          }
          return {
            ...row,
            status: translateExciseClaimStatus(row.status, t),
            claimDate: formatExciseDateForExport(row.claimDate, row.claimCode),
            reimbursementDate: formatExciseDateForExport(row.reimbursementDate, row.claimCode),
            payDateSortable: payoutDate,
            liters: row.liters ? Number(row.liters) : 0,
            countryId: selectedCountry ? selectedCountry.countryCode : "",
            currency: selectedCurrency ? selectedCurrency.isoCode : "",
            preFinanceId: row.preFinanceId > 0 ? t("Yes") : t("No"),
            preFinanceDate: preFinance && preFinance.payoutDate ? formatDisplayDate(preFinance.payoutDate) : "",
          };
        });
      default:
        return data;
    }
  };

  const applyExcelDateColumnFormats = (worksheet, dateFields) => {
    if (!worksheet["!ref"] || dateFields.length === 0) {
      return;
    }

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    dateFields.forEach((field) => {
      const colIndex = filteredColumns.findIndex((col) => col.field === field);
      if (colIndex === -1) {
        return;
      }

      const colLetter = XLSX.utils.encode_col(colIndex);

      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellRef = `${colLetter}${row + 1}`;
        const cell = worksheet[cellRef];
        if (!cell) {
          continue;
        }

        const parsed = parseExportDate(cell.v);
        if (!parsed) {
          continue;
        }

        worksheet[cellRef] = {
          t: "d",
          v: parsed,
          z: EXCEL_DATE_FORMAT,
        };
      }
    });
  };

  const applyNumberColumnFormat = (worksheet, field, format) => {
    const colIndex = filteredColumns.findIndex((col) => col.field === field);
    if (colIndex === -1 || !worksheet["!ref"]) {
      return;
    }

    const colLetter = XLSX.utils.encode_col(colIndex);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellRef = `${colLetter}${row + 1}`;
      if (worksheet[cellRef]) {
        worksheet[cellRef].z = format;
      }
    }
  };

  const applyNumericColumnFormat = (worksheet, field, format) => {
    const colIndex = filteredColumns.findIndex((col) => col.field === field);
    if (colIndex === -1 || !worksheet["!ref"]) {
      return;
    }

    const colLetter = XLSX.utils.encode_col(colIndex);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellRef = `${colLetter}${row + 1}`;
      const cell = worksheet[cellRef];
      if (!cell) {
        continue;
      }

      cell.z = format;
      cell.t = "n";
      if (typeof cell.v === "string") {
        cell.v = parseFloat(cell.v);
      }
    }
  };

  const handleExportCSV = () => {
    const transformedData = transformData(tableData);
    const dateFields = EXPORT_DATE_FIELDS[exportType] || [];
    const csvData = [
      filteredColumns.map((column) => column.headerName),
      ...transformedData.map((row) =>
        filteredColumns.map((column) => {
          const value = row[column.field] ?? "";
          if (dateFields.includes(column.field)) {
            return formatExportDateForCsv(value, nonDateExportLabels);
          }
          return value;
        })
      ),
    ];

    const blob = new Blob([csvData.map((row) => row.join(";")).join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `export_${exportType}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportXLSX = () => {
    const transformedData = transformData(tableData);
    const dateFields = EXPORT_DATE_FIELDS[exportType] || [];
    const xlsxData = transformedData.map((row) =>
      filteredColumns.reduce((acc, column) => {
        const value = row[column.field] ?? "";
        acc[column.headerName] = dateFields.includes(column.field) ? toExcelDateCellValue(value) : value;
        return acc;
      }, {})
    );

    const worksheet = XLSX.utils.json_to_sheet(xlsxData, { cellDates: true });
    applyExcelDateColumnFormats(worksheet, dateFields);

    if (exportType === "invoices") {
      applyNumberColumnFormat(worksheet, "vatAmount", "#,##0.00");
    }

    if (exportType === "claims") {
      applyNumberColumnFormat(worksheet, "claimVATAmountEUR", '"€ "#,##0.00');
    }

    if (exportType === "exciseClaims") {
      applyNumericColumnFormat(worksheet, "liters", "#,##0.00");
      applyNumericColumnFormat(worksheet, "claimAmountLocal", "#,##0.00");
      applyNumericColumnFormat(worksheet, "claimAmountEUR", '"€ "#,##0.00');
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `export_${exportType}.xlsx`);
  };

  return (
    <>
      <Tooltip title={t("Export to CSV")}>
        <IconButton color="primary" onClick={handleExportCSV}>
          <CsvIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("Export to XLSX")}>
        <IconButton color="primary" onClick={handleExportXLSX}>
          <XlsxIcon />
        </IconButton>
      </Tooltip>
    </>
  );
};

export default DataGridExport;
